/**
 * useMetaTags — updates <title>, <meta name="description">, and OpenGraph/Twitter
 * tags for the current page. Restores previous values on unmount.
 * Kept dependency-free (no react-helmet) to minimise bundle bloat.
 */
import { useEffect } from 'react';

const OG_KEYS = [
  ['property', 'og:title'],
  ['property', 'og:description'],
  ['property', 'og:url'],
  ['property', 'og:type'],
  ['name', 'twitter:title'],
  ['name', 'twitter:description'],
  ['name', 'description'],
];

function setMeta(attr, key, value) {
  if (value == null) return null;
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  const previous = el?.getAttribute('content') ?? null;
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', value);
  return previous;
}

export default function useMetaTags({ title, description, url, type = 'website' } = {}) {
  useEffect(() => {
    const prevTitle = document.title;
    if (title) document.title = title;

    const map = {
      'og:title': title,
      'og:description': description,
      'og:url': url || window.location.href,
      'og:type': type,
      'twitter:title': title,
      'twitter:description': description,
      description,
    };
    const previous = {};
    for (const [attr, key] of OG_KEYS) {
      previous[key] = setMeta(attr, key, map[key]);
    }

    return () => {
      document.title = prevTitle;
      for (const [attr, key] of OG_KEYS) {
        if (previous[key] != null) {
          document.head.querySelector(`meta[${attr}="${key}"]`)?.setAttribute('content', previous[key]);
        }
      }
    };
  }, [title, description, url, type]);
}
